import { httpClient } from "../../../shared/api/httpClient";
import type {
  CursorPaginatedResponse,
  PaginatedResponse,
} from "../../../shared/api/pagination";
import type {
  OriginOption,
  ProductAttribute,
  ProductOption,
  ProductResponse,
  ShipperOption,
} from "../model/types";

export const fetchProducts = async (
  page = 1,
  pageSize = 100,
  search?: string,
): Promise<PaginatedResponse<ProductResponse>> => {
  const response = await httpClient.get<PaginatedResponse<ProductResponse>>(
    "/products",
    {
      params: {
        page,
        page_size: pageSize,
        search: search?.trim() || undefined,
      },
    },
  );
  return response.data;
};

export const fetchUniqueProducts = async (): Promise<ProductOption[]> => {
  const response = await fetchProducts(1, 100);
  return response.items.map((product) => ({
    key: product.product_code,
    label: product.product_name
      ? `${product.product_code} - ${product.product_name}`
      : product.product_code,
    product,
  }));
};

export const searchProducts = (search: string) => fetchProducts(1, 100, search);

type SuggestionQuery = {
  search?: string;
  cursor?: string;
  limit?: number;
};

export const fetchProductAttributeOptions = async (
  productCode: string,
  attribute: ProductAttribute,
  { search, cursor, limit = 20 }: SuggestionQuery = {},
): Promise<CursorPaginatedResponse<string>> => {
  const response = await httpClient.get<CursorPaginatedResponse<string>>(
    `/products/${encodeURIComponent(productCode)}/attributes/${attribute}`,
    {
      params: {
        search: search?.trim() || undefined,
        cursor: cursor || undefined,
        limit,
      },
    },
  );
  return response.data;
};

export const fetchOriginsByProductCode = async (
  productCode: string,
  search?: string,
): Promise<OriginOption[]> => {
  const response = await fetchProductAttributeOptions(
    productCode,
    "production",
    { search },
  );
  return response.items.map((name) => ({
    origin_code: name,
    origin_name: name,
  }));
};

export const fetchShippersByProductAndOrigin = async (
  productCode: string,
  originCode: string,
  { search, cursor, limit = 20 }: SuggestionQuery = {},
): Promise<CursorPaginatedResponse<ShipperOption>> => {
  const response = await httpClient.get<CursorPaginatedResponse<string>>(
    `/products/${encodeURIComponent(productCode)}/shippers`,
    {
      params: {
        production: originCode.trim(),
        search: search?.trim() || undefined,
        cursor: cursor || undefined,
        limit,
      },
    },
  );
  return {
    ...response.data,
    items: response.data.items.map((name) => ({
      key: name,
      shipper_code: name,
      shipper_name: name,
    })),
  };
};
